import { HttpClient } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { CurrentTenantData } from '../../../shared/interfaces/current-tenant-data.interface';
import { SuvoUsersClientLibSettingsService } from '../../../shared/services/suvo-users-client-lib-settings/suvo-users-client-lib-settings.service';
import { ITenant } from '../interfaces/tenant.interface';

/**
 * @deprecated This has been replaced by the `ACTIVE_SHOP_DATA` injectable refreshable signal. The
 *   active shop can be changed by setting the `ACTIVE_SHOP_INIT` injectable writable signal.
 *   `ACTIVE_SHOP_DATA` is automatically refreshed when `ACTIVE_SHOP_INIT` changes.
 */
@Injectable({ providedIn: 'root' })
export class CurrentTenantService {
  /** @deprecated Use `inject(ACTIVE_SHOP_DATA)()`. */
  readonly currentTenant = signal<CurrentTenantData>(null);

  /** @deprecated Use `inject(ACTIVE_SHOP_DATA)().plan`. */
  readonly currentTenantPlan = computed(() => this.currentTenant()?.plan ?? 'FREE');

  /** @deprecated Use `inject(ACTIVE_SHOP_DATA)()`. */
  currentTenantData: CurrentTenantData = null;

  /** @deprecated Use `inject(ACTIVE_SHOP_DATA)()`. */
  currentFirebaseTenantId: string;

  constructor(
    private readonly http: HttpClient,
    private readonly settings: SuvoUsersClientLibSettingsService,
  ) {}

  /** @deprecated Use `inject(ACTIVE_SHOP_INIT).set`. */
  async init(url: string) {
    // Get initial part of config from users microservice
    this.currentTenantData = await lastValueFrom(
      this.http.get<Omit<CurrentTenantData, 'stripe_pk' | 'autoTrialPeriod'>>(url, {
        headers: { 'Platform-ID': this.settings.platformId },
      }),
    );

    // Get another part of config from subscriptions microservice
    const subscriptionSettings = await lastValueFrom(
      this.http.get<{
        _id: string;
        stripeAccountId?: string;
        autoSubscribeFreeProducts?: boolean;
        redirectToEmailAlerts?: boolean;
        stripePublishableKey?: string;
        stripeConnectionType?: 'standard' | 'express';
        autoTrialPeriod?: number;
        chartColorPalette?: string[];
        paymentContactName?: string;
        paymentContactEmail?: string;
      }>(
        `${this.settings.subscriptionServiceApi}tenant/${this.currentTenantData._id}/subscription-settings`,
      ),
    );

    // Combine config
    if (subscriptionSettings) {
      const { _id, ...restOfSubscriptionSettings } = subscriptionSettings;
      this.currentTenantData = {
        ...this.currentTenantData,
        ...restOfSubscriptionSettings,
        stripe_pk: restOfSubscriptionSettings.stripePublishableKey,
      };
    }

    this.currentTenant.set(this.currentTenantData);
  }

  async update(tenant: Partial<ITenant>) {
    const currentTenant = this.currentTenant() ?? this.currentTenantData;
    const tenantId = currentTenant?._id;

    if (!tenantId) {
      throw new Error('Current tenant is not initialized yet.');
    }

    const updatedTenant = await lastValueFrom(
      this.http.patch<Partial<CurrentTenantData>>(
        `${this.settings.usersServiceApi}tenant/${tenantId}`,
        tenant,
      ),
    );

    const nextTenant = {
      ...currentTenant,
      ...tenant,
      ...updatedTenant,
      theme: {
        ...currentTenant?.theme,
        ...tenant.theme,
        ...updatedTenant?.theme,
      },
    } as CurrentTenantData;

    this.currentTenantData = nextTenant;
    this.currentTenant.set(nextTenant);

    return updatedTenant;
  }
}
